import React, { useCallback, useState } from "react";
import { Edit, PictureInPictureOutlined, Settings } from "@mui/icons-material";
import { Alert, AlertTitle, Button } from "@mui/material";
import { useSnackbar } from "notistack";

import { Progress } from "ui";

import { useGetVersionQuery, useUpdateVersionArchitectureMutation } from "fond/api";
import { DEFAULT_DATA_PREP_CONFIGURATION } from "fond/architecture/dataPrepConfiguration";
import { summarizeDataPrepConfiguration } from "fond/architecture/designPreferences/cableOptions";
import { useFeatureFlag } from "fond/featureFlags";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { useGetPlannerLayers } from "fond/project/panels/ExtendedDesign/hooks/useGetPlannerLayers";
import { loadProject, openArchitectureModal } from "fond/project/redux";
import { Project, WORKFLOW_KEYS } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";
import { StackedNavigationButton, useStackedNavigationContext } from "fond/widgets";

import { DataPrepGenerateOptions, toDataPrepRunParameters } from "./dataPrepRunParameters";
import RunDataPrep from "./RunDataPrep";

const stageConfig: Record<string, { label: string }> = {
  clean: { label: "Cleaning data" },
  ug_network: { label: "Generating underground network" },
  leadins: { label: "Generating leadins" },
  outputs: { label: "Preparing outputs" },
};

interface DataPrepPanelProps {
  project: Project;
  versionId: string;
  /**
   * Flag indicating if the project is read only
   */
  readOnly: boolean;
}

const DataPrepPanel: React.FC<DataPrepPanelProps> = ({ project, versionId, readOnly }: DataPrepPanelProps) => {
  const dispatch = useAppDispatch();
  const { open } = useStackedNavigationContext();
  const { data: version } = useGetVersionQuery(versionId ?? "", { skip: !versionId });
  const [updateVersionArchitecture, { isLoading: isSavingArchitecture }] = useUpdateVersionArchitectureMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { layerKeys } = useGetPlannerLayers(versionId);
  const { value: canReviewProblematicParcels } = useFeatureFlag("street_parcel_identification");
  const [generate, setGenerate] = useState<DataPrepGenerateOptions>({ underground: true, leadins: true });

  const addressLayer = layerKeys.includes("input_layers/demand");
  const parcelLayer = layerKeys.includes("input_layers/parcel");
  const undergroundLayer = layerKeys.includes("input_layers/streets");

  const handleOnComplete = useCallback(async () => {
    // TODO: we should probably go to the 'Design' step automagically once this finishes?
    await dispatch(loadProject({ uuid: project.ID, forceRefetch: true }));
  }, [dispatch, project.ID]);

  const { workflowState, stages, runWorkflow, cancelWorkflow } = useCustomWorkflow({
    versionId,
    key: WORKFLOW_KEYS.PLANNER_EXTENDED.DATA_PREP,
    onComplete: handleOnComplete,
  });

  const isDataPrepping = workflowState === "STARTING" || workflowState === "IN_PROGRESS";
  const canDataPrep =
    addressLayer &&
    undergroundLayer &&
    parcelLayer &&
    Boolean(runWorkflow) &&
    Boolean(version?.Architecture) &&
    !isDataPrepping &&
    !isSavingArchitecture;
  const designPreferencesSummary = version?.Architecture
    ? summarizeDataPrepConfiguration(version.Architecture.DataPrepConfiguration ?? DEFAULT_DATA_PREP_CONFIGURATION)
    : undefined;

  const handleOpenSettings = () => {
    dispatch(openArchitectureModal("DesignPreferences"));
  };

  const handleDataPrep = async () => {
    if (!runWorkflow) {
      return;
    }

    // Architectures saved outside planner_extended may lack a data prep configuration, so ensure we set one.
    if (version?.Architecture && !version.Architecture.DataPrepConfiguration) {
      try {
        await updateVersionArchitecture({ versionId, architecture: { DataPrepConfiguration: DEFAULT_DATA_PREP_CONFIGURATION } }).unwrap();
      } catch {
        enqueueSnackbar("Failed to save Data Prep Settings.", { variant: "error" });
        return;
      }
    }
    runWorkflow(toDataPrepRunParameters(generate));
  };

  const handleCancelDataPrep = () => {
    cancelWorkflow?.();
  };

  const handleModifyData = () => {
    open("extended-design-modifications");
  };

  const handleIdentifyStreetParcels = () => {
    open("identify-street-parcels");
  };

  return (
    <>
      {workflowState === "COMPLETE" && (
        <Alert severity="success" variant="compact" sx={{ mb: 1 }}>
          Data preparation complete.
        </Alert>
      )}

      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Data preparation failed</AlertTitle>
          Data could not be generated.
        </Alert>
      )}

      {!isDataPrepping && (
        <>
          {canReviewProblematicParcels && (
            <StackedNavigationButton
              data-testid="identify-street-parcels-navigation"
              icon={<PictureInPictureOutlined />}
              title="Review problematic parcels"
              onClick={handleIdentifyStreetParcels}
              disabled={readOnly}
              sx={{ mt: 0, mb: 1, "&:last-child": { mb: 0 } }}
              iconSize="small"
              variant="outlined"
            />
          )}

          <StackedNavigationButton
            data-testid="data-prep-settings-navigation"
            icon={<Settings />}
            title="Design preferences"
            subtitle={designPreferencesSummary}
            onClick={handleOpenSettings}
            disabled={readOnly}
            sx={{ mt: 0, mb: 1, "&:last-child": { mb: 0 } }}
            iconSize="small"
            variant="outlined"
          />

          <RunDataPrep
            layerKeys={layerKeys}
            canRun={canDataPrep}
            isRerun={workflowState === "COMPLETE"}
            readOnly={readOnly}
            generate={generate}
            onGenerateChange={setGenerate}
            onRun={handleDataPrep}
          />
        </>
      )}

      {stages && (isDataPrepping || workflowState === "ERROR") && (
        <Progress steps={stages.map(({ key, status }) => ({ ID: key, Label: stageConfig[key]?.label ?? key, Status: status }))} />
      )}

      {isDataPrepping && (
        <Button fullWidth variant="contained" size="small" sx={{ my: 1 }} disabled={!cancelWorkflow || readOnly} onClick={handleCancelDataPrep}>
          Cancel
        </Button>
      )}

      {workflowState === "COMPLETE" && !isDataPrepping && (
        <StackedNavigationButton icon={<Edit />} title="Modify data" onClick={handleModifyData} disabled={readOnly} sx={{ m: 0, mt: 1 }} />
      )}
    </>
  );
};

export default DataPrepPanel;
